import { alpha, Skeleton, Typography } from "@mui/material";
import { Folder, FolderApi } from "certiblok-api-manager";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import CertiblokIcon from "../icons/CertiblokIcon";
import WhitePopperTooltip from "../poppers/WhitePopperTooltip";
import UserAvatar from "../UserAvatar";
import { useQuery } from "@tanstack/react-query";
import { instanceApi } from "../../hooks/api";

export const reservedFolderQueries = {
	base: ["reserved-folder"],
	byId: (id: string) => [...reservedFolderQueries.base, id],
};

const ReservedFolderWhiteTooltip = ({
	enabled,
	folder: _folder,
	children,
}: {
	enabled: boolean;
	folder: Folder;
	children: ReactNode;
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);

	const { data: fetchedFolder, isLoading } = useQuery({
		queryKey: reservedFolderQueries.byId(_folder.id),
		queryFn: () => instanceApi(FolderApi).getPlainFolder(_folder.id),
		enabled: enabled && isOpen,
		select: (res) => res.data,
		retry: false,
		refetchOnWindowFocus: false,
	});

	const folder = fetchedFolder ?? _folder;

	return (
		<WhitePopperTooltip
			enabled={enabled}
			onOpenChange={setIsOpen}
			title={
				isLoading ? (
					[...Array(3)].map((_, index) => <Skeleton key={index} className="h-6 w-28 rounded-md" />)
				) : folder.reservedToTeams || folder.reservedToUsers ? (
					<div className="flex flex-col gap-1">
						{(folder.reservedToTeams ?? []).map((team) => (
							<div className="flex gap-2 items-center" key={team.id}>
								<div
									className={"w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"}
									style={{ backgroundColor: alpha(`${team.color}`, 0.8) }}
								>
									<CertiblokIcon size={12} name="profile_outline_01" color="white" />
								</div>
								<Typography variant="label" noWrap color="rgba(0,0,0,0.84)">
									{team.name}
								</Typography>
							</div>
						))}
						{(folder.reservedToUsers ?? []).map((user) => (
							<div className="flex gap-2 items-center" key={user.id}>
								<UserAvatar user={user} sx={{ width: 16, height: 16 }} />
								<Typography variant="label" noWrap color="rgba(0,0,0,0.84)" key={user.id}>
									{user.name} {user.surname}
								</Typography>
							</div>
						))}
					</div>
				) : (
					t("folders.reserved.thisFolderIsReserved")
				)
			}
		>
			{children}
		</WhitePopperTooltip>
	);
};

export default ReservedFolderWhiteTooltip;
